import React from "react";
import formatDate from "../../../utils/formatDate";
import Button from "../../../components/Button/Button";
import { FaCreditCard, FaCoins, FaBell } from "react-icons/fa";
import { toAbsoluteArticlePhotoUrl } from "../../../services/articlesPhotoServices";

function hasEcheanceTag(msg = "") {
  const clean = msg
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return clean.includes("echeance");
}

const ArticleTable = ({
  articles,
  getStatusColor,
  defaultPhotoUrl,
  handlePayerClick,
  handlePayerEnPlusieursFoisClick,
  payingId,
  handleNotifyClick,
  notifyingId,
  isAlreadyNotified,
}) => {
  return (
    <table className="min-w-full bg-white divide-y divide-gray-200 font-lato hidden sm:table">
      {articles.length > 0 ? (
        <tbody className="divide-y divide-gray-200 font-montserrat">
          {articles.map((article) => {
            const isDisabled = article.autorisation_achat === false;
            const canShowButtons =
              ["Non Réglé", "En Attente"].includes(article.statut_reglement);

            const isInstallment = hasEcheanceTag(article.message_achat);
            const canShowInstallmentButton =
              article.article_id === null && !isInstallment;

            // Résout correctement les URLs relatives (/uploads/...) en URLs absolues
            const rawPhoto = article.Article?.photo_url;
            const imgSrc = rawPhoto
              ? toAbsoluteArticlePhotoUrl(rawPhoto)
              : defaultPhotoUrl;

            return (
              <tr
                key={article.id}
                className="dark:bg-slate-200 hover:bg-slate-200 dark:hover:bg-slate-400 dark:hover:text-slate-200 cursor-pointer"
              >
                {/* Date */}
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(article.date_achat)}
                </td>

                {/* Détails / statut */}
                <td className="px-4 py-2 text-sm text-gray-500">
                  <div className="font-semibold">
                    {`${article.quantite} x ${
                      article.article_id === null
                        ? "Règlement Licence-Cotisation"
                        : article.Article
                        ? article.Article.nom
                        : "Nom non disponible"
                    }`}
                  </div>
                  <div className="text-xs italic text-gray-800">
                    {article.message_achat}
                  </div>
                  <span
                    className={`inline-block my-2 px-3 py-1 rounded-full font-semibold text-xs ${getStatusColor(
                      article.statut_reglement
                    )}`}
                  >
                    {article.statut_reglement}
                  </span>
                </td>

                {/* Photo */}
                <td className="px-4 py-2 text-sm text-gray-500">
                  <img
                    src={imgSrc}
                    alt={
                      article.article_id === null
                        ? "Règlement Licence-Cotisation"
                        : article.Article?.nom || "Article"
                    }
                    className="w-32 h-32 object-contain aspect-square"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultPhotoUrl;
                    }}
                  />
                </td>

                {/* Prix */}
                <td className="px-4 py-2 min-w-[90px] whitespace-nowrap text-xl font-bold text-gray-500">
                  {`${article.prix_total} €`}
                </td>

                {/* Actions */}
                <td className="text-center">
                  <div className="flex items-center">
                    {canShowButtons && (
                      <>
                        {isDisabled && (
                          <span className="text-xs text-red-700 italic mr-2">
                            Paiement non activé
                          </span>
                        )}

                        <Button
                          text={payingId === article.id ? "Redirection…" : "Payer"}
                          icon={FaCreditCard}
                          onClick={
                            isDisabled ? undefined : () => handlePayerClick(article.id)
                          }
                          disabled={isDisabled || payingId === article.id}
                          className={`ml-4 gap-2 ${
                            isDisabled ? "opacity-40 cursor-not-allowed" : ""
                          }`}
                        />

                        {canShowInstallmentButton && (
                          <Button
                            text="Payer en plusieurs fois"
                            icon={FaCoins}
                            onClick={
                              isDisabled
                                ? undefined
                                : () => handlePayerEnPlusieursFoisClick(article.id)
                            }
                            disabled={isDisabled}
                            className={`ml-4 gap-2 whitespace-nowrap ${
                              isDisabled ? "opacity-40 cursor-not-allowed" : ""
                            }`}
                          />
                        )}
                      </>
                    )}

                    {/* 🔔 Bouton de notification si “Réglé” */}
                    {article.statut_reglement === "Réglé" &&
                      (isAlreadyNotified?.(article.id) ? (
                        <span className="ml-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          Admin prévenu
                        </span>
                      ) : (
                        <Button
                          text={notifyingId === article.id ? "Envoi…" : "Prévenir l'admin"}
                          icon={FaBell}
                          onClick={() => handleNotifyClick?.(article)}
                          disabled={notifyingId === article.id}
                          className="ml-4 gap-2"
                        />
                      ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      ) : (
        <tbody>
          <tr>
            <td colSpan="5" className="text-center py-4 px-4 dark:text-black">
              Pas d'articles commandés
            </td>
          </tr>
        </tbody>
      )}
    </table>
  );
};

export default ArticleTable;
